/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';


import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {RouteTwoPanelLayoutComponent} from '../common/directives/route-two-panel-layout.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {LineItemEditPageComponent} from './line-item-edit-page.component';
import {LineItemModel} from './line-item-api.service';
import {LineItemTemplateAnalysisPageComponent} from './line-item-template-analysis-page.component';
import {LineItemTemplateApiService} from './line-item-template-api.service';
import {LineItemTemplateContextActionsComponent} from './line-item-template-context-actions.component';
import {LineItemTemplateListPageComponent} from './line-item-template-list-page.component';

export function lineItemTemplateStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(LineItemTemplateApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => {
    const sectionPerms = permissions.getSectionPerms();
    if (Object.prototype.hasOwnProperty.call(sectionPerms, 'lineItemsEditable')) {
      return !sectionPerms.lineItemsEditable;
    }
    return Object.prototype.hasOwnProperty.call(sectionPerms, 'lineItems') ? !sectionPerms.lineItems : false;
  };
  const rh = adnRouterHelper.createRouterHelper(
    'line-item-templates.line-item-template', LineItemTemplateApiService, 'app.adv', 'app'
  );

  states.add(rh.listPath(), rh.list({
    resolve: {
      sectionParam: function() {
        return 'lineItems';
      }
    },
    views: {
      'content@app': {component: LineItemTemplateListPageComponent, bindings: {readOnly: 'listReadOnly'}}
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      advertiserFromParam: function() {
        return undefined;
      },
      orderFromParam: function() {
        return undefined;
      },
      lineItemView: function() {
        return null;
      },
      full: function() {
        return false;
      },
      viewType: function() {
        return 1;
      },
      lineItemTemplatePage: function($stateParams: any) {
        const createState = (model: LineItemModel, isNew: boolean) => new SignalAdminResourcePageState(
          model, isNew, readOnly(), api, 'Line Item Template', 'name', undefined, undefined,
          {objectTypeKey: 'LineItemTemplate', crumbParams: []},
          {
            warningMessage: 'The {{action}} of the Line Item Template will not affect existing line items created from it.',
            object: 'Line Item Template'
          }
        );
        if ($stateParams.isNew) {
          return createState({
            id: $stateParams.id,
            resourceName: 'lineItemTemplate',
            objectState: 'ACTIVE',
            validationWarnings: []
          }, true);
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => createState({...model, resourceName: 'lineItemTemplate'}, false));
      },
      model: (lineItemTemplatePage: SignalAdminResourcePageState<LineItemModel>) => lineItemTemplatePage.model()
    },
    views: {
      'content@app': {
        component: RouteTwoPanelLayoutComponent,
        bindings: {full: 'full', state: 'lineItemTemplatePage'}
      },
      'rightPanel@app.adv.line-item-templates.line-item-template': {
        component: LineItemTemplateAnalysisPageComponent,
        bindings: {state: 'lineItemTemplatePage'}
      },
      'leftPanel@app.adv.line-item-templates.line-item-template': {
        component: LineItemEditPageComponent,
        bindings: {
          state: 'lineItemTemplatePage', full: 'full', viewType: 'viewType',
          orderFromParam: 'orderFromParam', advertiserFromParam: 'advertiserFromParam', lineItemView: 'lineItemView'
        }
      },
      'context@app': {
        component: LineItemTemplateContextActionsComponent,
        bindings: {state: 'lineItemTemplatePage'}
      },
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'lineItemTemplatePage'}}
    }
  }, 'LineItemTemplate'));
  return states.build();
}
